import React from "react";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
 import VideoHero from "../components/hero/VideoHero";
import SEO from "../components/common/SEO";

const STEPS = [
    { n: "01", title: "Light Generation", body: "A laser source emits controlled photons." },
    { n: "02", title: "Propagation", body: "Light travels through atmosphere (scatter, attenuation)." },
    { n: "03", title: "Interaction", body: "Reflection (primary), partial absorption, surface-dependent response." },
    { n: "04", title: "Return Signal", body: "Reflected photons return to the sensor." },
    { n: "05", title: "Detection", body: "Photodetector converts photons → electrical signal." },
    { n: "06", title: "Interpretation", body: "Time delay + intensity → distance + structure." },
];

const FIBER_STEPS = [
    { n: "S1", t: "Pumping", b: "External laser injects energy into doped fiber." },
    { n: "S2", t: "Excitation", b: "Rare-earth ions absorb energy → excited state." },
    { n: "S3", t: "Stimulated Emission", b: "Incoming photons trigger emission of identical photons." },
    { n: "S4", t: "Amplification", b: "Light is amplified as it travels through fiber." },
    { n: "S5", t: "Resonance", b: "Cavity builds coherent laser output." },
];

export default function Technologies() {
    return (
        <>
            <SEO
                title="Technologies — LiDAR, Photonics & GeoAI · Skovomax"
                description="Photon-grade sensing: eye-safe 1550 nm fiber laser sources, coherent (FMCW) LiDAR, hyperspectral imaging, and AI-driven point-cloud + geospatial analytics."
            />
            <section className="relative min-h-[80vh] flex items-center border-b border-white/10 overflow-hidden pt-24 pb-24">
                <VideoHero src="https://customer-assets.emergentagent.com/job_sensor-intelligence/artifacts/10p2zrcq_Laser_beam_entering_202604211521.mp4">
                    <div className="absolute inset-0 photonic-grid opacity-30" />
                </VideoHero>
                <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 w-full">
                    <Reveal>
                        <SectionLabel label="Technologies / 01" />
                        <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                            Photon-grade <br />
                            <span className="italic text-white/70">measurement systems.</span>
                        </h1>
                        <p className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
                            Each Skovomax technology is engineered as a complete optical system — from the
                            physics of photon generation, through propagation and target interaction, to
                            detection, timing, and information reconstruction.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-6 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/55">
                            <span className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-indigo)] animate-pulse" />
                                λ 1550 nm · Coherent
                            </span>
                            <span className="text-white/30">/</span>
                            <span>Signal · Stable</span>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* LiDAR deep dive */}
            <section id="lidar" className="py-28 border-b border-white/10 scroll-mt-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
                        <div className="md:col-span-5">
                            <SectionLabel number="01" label="Photonic LiDAR" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
                                LiDAR is a <br />
                                <span className="italic text-white/70">closed-loop light system.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-6 md:col-start-7 flex items-end">
                            <p className="text-white/60 leading-relaxed text-lg">
                                Photon emission → interaction → return → detection → timing → reconstruction.
                                From a pure photonics viewpoint, LiDAR is not a sensor — it is a laser-based
                                measurement system that captures the geometry and structure of the physical world.
                            </p>
                        </div>
                    </div>

                    {/* Annotated LiDAR anatomy figure */}
                    <Reveal>
                        <figure className="mb-16">
                            <div className="frame-corners bg-[#0a0a18] p-4 md:p-6 border border-white/5">
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/6a030c594aed6d35557e8fb7`}
                                    alt="Anatomy of a Photonic LiDAR — labeled diagram of the optical and electronic subsystems: laser source, beam steering, propagation, photon detection, and signal processing."
                                    className="w-full block"
                                    loading="lazy"
                                    data-testid="lidar-anatomy-figure"
                                />
                            </div>
                            <figcaption className="mt-4 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/45 text-center">
                                Anatomy of a Photonic LiDAR — from photon generation to point-cloud
                            </figcaption>
                        </figure>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
                        {STEPS.map((s, i) => (
                            <Reveal delay={i * 60} key={s.n}>
                                <div className="p-10 bg-[#050510] h-full">
                                    <div className="font-mono-tech text-[11px] tracking-[0.22em] text-[var(--accent-indigo)] mb-4">
                                        STEP {s.n}
                                    </div>
                                    <h3 className="font-serif-display text-2xl mb-3">{s.title}</h3>
                                    <p className="text-white/60 leading-relaxed">{s.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Photonic Sensors */}
            <section id="sensors" className="py-28 border-b border-white/10 bg-[#0a0a18] scroll-mt-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
                        <div className="md:col-span-5">
                            <SectionLabel number="02" label="Photonic Sensors" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
                                Closed-loop <br />
                                <span className="italic text-white/70">optical architectures.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-6 md:col-start-7 flex items-end">
                            <p className="text-white/60 leading-relaxed text-lg">
                                A photonic sensor is an engineered pipeline — laser source, beam shaping, target
                                interaction, photodetection, and signal processing — treated as a single optical
                                system. Range, velocity, and material signatures fall out of the physics.
                            </p>
                        </div>
                    </div>

                    {/* Annotated Photonic Sensor anatomy figure */}
                    <Reveal>
                        <figure className="mb-16">
                            <div className="frame-corners bg-[#0a0a18] p-4 md:p-6 border border-white/5">
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/6a042810a3991be6fb4e7ad2`}
                                    alt="Anatomy of a Photonic Sensor — labeled diagram showing the photonic integrated circuit, CMOS photodiode array, micro-optics interface, smart electronics, and the layer-by-layer exploded stack from protective window down to the high-speed interface."
                                    className="w-full block"
                                    loading="lazy"
                                    data-testid="sensors-anatomy-figure"
                                />
                            </div>
                            <figcaption className="mt-4 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/45 text-center">
                                Anatomy of a Photonic Sensor — from waveguide to AI processing engine
                            </figcaption>
                        </figure>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/10">
                        {[
                            { n: "A", title: "Laser Source", body: "Coherent emission — fiber lasers, DFB diodes, narrow-linewidth sources for phase-stable measurement." },
                            { n: "B", title: "Beam Shaping", body: "Collimators, MEMS / galvo scanners, diffractive optics that control divergence and spot geometry." },
                            { n: "C", title: "Target Interaction", body: "Reflection, absorption, and scattering carry the information the measurement is trying to extract." },
                            { n: "D", title: "Photodetection", body: "APDs, SPADs, and balanced photodiodes convert returning photons into electrical signal with timing fidelity." },
                            { n: "E", title: "Signal Processing", body: "Time-of-flight, FMCW, or coherent phase pipelines reconstruct distance, velocity, and structure." },
                        ].map((s, i) => (
                            <Reveal delay={i * 60} key={s.n}>
                                <div className="p-8 bg-[#0a0a18] h-full">
                                    <div className="font-mono-tech text-[11px] tracking-[0.22em] text-[var(--accent-indigo)] mb-4">
                                        BLOCK {s.n}
                                    </div>
                                    <h3 className="font-serif-display text-2xl mb-3 leading-tight">{s.title}</h3>
                                    <p className="text-white/60 leading-relaxed text-sm">{s.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                        <TechCard className="!p-8">
                            <div className="tech-label mb-3">Modalities</div>
                            <ul className="space-y-3 text-white/70">
                                <li><span className="text-white">Time-of-Flight —</span> direct photon travel time → range.</li>
                                <li><span className="text-white">FMCW —</span> frequency-modulated coherent detection → range + velocity.</li>
                                <li><span className="text-white">Phase-based —</span> modulated intensity phase shift → short-range precision.</li>
                            </ul>
                        </TechCard>
                        <TechCard className="!p-8">
                            <div className="tech-label mb-3">Performance axes</div>
                            <ul className="space-y-3 text-white/70">
                                <li><span className="text-white">Range / SNR —</span> source power, detector sensitivity, aperture.</li>
                                <li><span className="text-white">Eye-safety —</span> 1550 nm operating window.</li>
                                <li><span className="text-white">Coherence —</span> narrow linewidth enables FMCW + interferometric sensing.</li>
                            </ul>
                        </TechCard>
                    </div>
                </div>
            </section>

            {/* Fiber laser */}
            <section id="fiber-laser" className="py-28 border-b border-white/10 scroll-mt-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
                        <div className="md:col-span-5">
                            <SectionLabel number="03" label="Fiber Laser Systems" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
                                Doped optical fiber <br />
                                <span className="italic text-white/70">as gain medium.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-6 md:col-start-7 flex items-end">
                            <p className="text-white/60 leading-relaxed text-lg">
                                The gain medium is an optical fiber doped with rare-earth elements — Ytterbium
                                (Yb³⁺), Erbium (Er³⁺), or Thulium (Tm³⁺). Instead of a bulk crystal, the fiber
                                itself generates and amplifies light, delivering diffraction-limited beams with
                                M² &lt; 1.1 at kilowatt power.
                            </p>
                        </div>
                    </div>

                    {/* Annotated Fiber Laser bench figure */}
                    <Reveal>
                        <figure className="mb-16">
                            <div className="frame-corners bg-[#0a0a18] p-4 md:p-6 border border-white/5">
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/6a043e19c14735fbb53a7f10`}
                                    alt="Anatomy of a Fiber Laser System — bench photograph annotated with each subsystem: high-brightness doped fiber gain medium, high-power laser diode, fiber Bragg grating, advanced fiber isolator, next-gen (2+1)×1 fiber combiner, real-time monitoring & control unit, beam delivery, and high-power 1000W / 1080nm output with M² < 1.1 diffraction-limited beam."
                                    className="w-full block"
                                    loading="lazy"
                                    data-testid="fiber-anatomy-figure"
                                />
                            </div>
                            <figcaption className="mt-4 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/45 text-center">
                                Anatomy of a Fiber Laser System — 1000W @ 1080nm with M² &lt; 1.1
                            </figcaption>
                        </figure>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/10">
                        {FIBER_STEPS.map((s, i) => (
                            <Reveal delay={i * 60} key={s.n}>
                                <div className="p-8 bg-[#0a0a18] h-full">
                                    <div className="font-mono-tech text-[11px] tracking-[0.22em] text-[var(--accent-indigo)] mb-4">
                                        STEP {s.n.replace("S", "")}
                                    </div>
                                    <h3 className="font-serif-display text-2xl mb-3 leading-tight">{s.t}</h3>
                                    <p className="text-white/60 leading-relaxed text-sm">{s.b}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    {/* Why fiber + applications — tucked under the mechanism grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-12">
                        <Reveal>
                            <TechCard className="!p-10 h-full">
                                <SectionLabel label="Why fiber lasers" />
                                <ul className="mt-6 space-y-4 text-white/70">
                                    <li><span className="text-white">High Efficiency —</span> excellent heat dissipation via fiber geometry.</li>
                                    <li><span className="text-white">Beam Quality —</span> highly coherent output.</li>
                                    <li><span className="text-white">Compact & Robust —</span> no complex bulk alignment.</li>
                                    <li><span className="text-white">Scalable Power —</span> low-power sensing to high-power industrial.</li>
                                </ul>
                            </TechCard>
                        </Reveal>
                        <Reveal delay={120}>
                            <TechCard className="!p-10 h-full">
                                <SectionLabel label="Integration" />
                                <ul className="mt-6 space-y-4 text-white/70">
                                    <li><span className="text-white">LiDAR systems —</span> stable laser source, eye-safe 1550 nm.</li>
                                    <li><span className="text-white">Environmental monitoring —</span> mid-IR sensing (Thulium).</li>
                                    <li><span className="text-white">Coherent LiDAR (FMCW) —</span> phase-accurate ranging.</li>
                                    <li><span className="text-white">Optical communication —</span> sensing networks, telemetry.</li>
                                </ul>
                            </TechCard>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* GeoAI */}
            <section id="geoai" className="py-28 scroll-mt-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10">
                        <div className="md:col-span-5">
                            <SectionLabel number="04" label="GeoAI" />
                            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
                                Pattern intelligence <br />
                                <span className="italic text-white/70">from spatial data.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-6 md:col-start-7 flex items-end">
                            <p className="text-white/65 leading-relaxed text-lg">
                                “Data are not just numbers, they are numbers with a <em className="text-white">context</em>.
                                In data analysis, context provides meaning.” — Cobb &amp; Moore, 1997. Geospatial
                                analytics adds timing and location to traditional data, revealing insights lost in spreadsheets.
                            </p>
                        </div>
                    </div>

                    {/* Annotated GeoAI architecture figure */}
                    <Reveal>
                        <figure className="mb-16">
                            <div className="frame-corners bg-[#0a0a18] p-4 md:p-6 border border-white/5">
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/6a044713c14735fbb53a7f18`}
                                    alt="Anatomy of GeoAI — multi-source Earth data (satellite, aerial, LiDAR, IoT) feeding geospatial AI models for land-use classification, change detection, hazard prediction, carbon and infrastructure monitoring, producing actionable insights for climate, disaster, smart-city, agriculture and natural-resource use cases."
                                    className="w-full block"
                                    loading="lazy"
                                    data-testid="geoai-anatomy-figure"
                                />
                            </div>
                            <figcaption className="mt-4 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-white/45 text-center">
                                Anatomy of GeoAI — better data → smarter AI → stronger decisions → sustainable future
                            </figcaption>
                        </figure>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { t: "Feature extraction from imagery", b: "CNNs + classical CV on hyperspectral, multispectral and SAR." },
                            { t: "Classification from point clouds", b: "Supervised and self-supervised segmentation of LiDAR scans." },
                            { t: "Deep learning for geoscience", b: "Spatial diffusion, gravity models, multi-criteria AHP/TOPSIS." },
                        ].map((c, i) => (
                            <Reveal delay={i * 80} key={c.t}>
                                <TechCard className="!p-8 h-full">
                                    <h3 className="font-serif-display text-2xl mb-3 leading-tight">{c.t}</h3>
                                    <p className="text-white/55 text-sm leading-relaxed">{c.b}</p>
                                </TechCard>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
